import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import type { NotificationEntry } from '../../domain/types';

interface NotificationItemProps {
  notification: NotificationEntry;
  onMarkRead: () => void;
  onArchive: () => void;
  isActionPending: boolean;
  actionError: string | null;
}

const categoryIcons: Record<string, typeof NotificationsNoneIcon> = {
  assignment: AssignmentIndIcon,
  case_request: GavelIcon,
  field_request: AssignmentIcon,
  call: PhoneIcon,
  evaluation: AssessmentIcon,
  system: SettingsIcon,
};

export function NotificationItem({ notification, onMarkRead, onArchive, isActionPending, actionError }: NotificationItemProps) {
  const categoryKey = notification.category?.toLowerCase() ?? '';
  const Icon = categoryIcons[categoryKey] ?? NotificationsNoneIcon;
  const timestamp = new Date(notification.createdAt);
  const timeLabel = Number.isNaN(timestamp.getTime()) ? 'Time unavailable' : formatDistanceToNow(timestamp, { addSuffix: true });
  const focusSx = { '&:focus-visible': { outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } };
  return (
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: notification.readAt ? 'background.paper' : 'action.hover', display: 'flex', gap: 1.25 }}>
      <Icon fontSize="small" color={notification.readAt ? 'disabled' : 'primary'} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" fontWeight={notification.readAt ? 400 : 600}>
            {notification.title}
          </Typography>
          {!notification.readAt && <Box aria-label="Unread" sx={{ width: 7, height: 7, flexShrink: 0, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.75 }} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {notification.message}
        </Typography>
        <Typography variant="caption" color="text.secondary" title={notification.createdAt}>
          {timeLabel}
        </Typography>
        {actionError && (
          <Typography role="alert" variant="caption" color="error.main" display="block">
            {actionError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          {!notification.readAt && (
            <Button size="small" disabled={isActionPending} onClick={onMarkRead} sx={focusSx}>
              Mark read
            </Button>
          )}
          <Button size="small" disabled={isActionPending} onClick={onArchive} sx={focusSx}>
            Archive
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
